import API_BASE_URL from "./apiConfig";
import { clearAuth, getToken } from "./auth";

const isApiUrl = (url) => String(url || "").startsWith(API_BASE_URL);

const getPathName = (pathOrUrl) => {
  try {
    if (String(pathOrUrl || "").startsWith("http")) {
      return new URL(pathOrUrl).pathname || "";
    }
    return String(pathOrUrl || "");
  } catch {
    return String(pathOrUrl || "");
  }
};

const isAdminRequest = (pathOrUrl, method = "GET") => {
  const path = getPathName(pathOrUrl).toLowerCase();
  const verb = String(method || "GET").toUpperCase();

  if (path.startsWith("/api/admin")) return true;
  if (path.startsWith("/questions")) return true;

  if (path.startsWith("/users")) {
    return path !== "/users/login" && path !== "/users/signup";
  }

  if (path.startsWith("/api/current-affairs-quizzes")) {
    return verb !== "GET";
  }

  if (path.startsWith("/api/jobs")) {
    return verb !== "GET";
  }

  if (path.startsWith("/api/master-jobs/scrape")) {
    return true;
  }

  if (path.startsWith("/api/master-jobs/ai-direct")) {
    return true;
  }

  if (path.startsWith("/api/master-jobs/scrape-and-save")) {
    return true;
  }

  if (path.startsWith("/api/master-jobs/auto-scrape-homepage")) {
    return true;
  }

  if (path.startsWith("/api/master-jobs/run-hourly-scheduler")) {
    return true;
  }

  if (path.startsWith("/api/master-jobs/admin")) {
    return true;
  }

  return false;
};

const onUnauthorized = () => {
  clearAuth();
  if (window.location.pathname !== "/login") {
    window.location.assign("/login");
  }
};

export const apiFetch = async (path, options = {}) => {
  const {
    auth = false,
    skipUnauthorizedHandler = false,
    timeoutMs,
    headers: customHeaders = {},
    ...rest
  } = options;

  const url = path.startsWith("http") ? path : `${API_BASE_URL}${path}`;
  const headers = { ...customHeaders };
  const method = String(rest?.method || "GET").toUpperCase();
  const shouldUseAuth = auth || isAdminRequest(path, method) || isAdminRequest(url, method);

  if (shouldUseAuth) {
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
  const timeoutId =
    controller && Number(timeoutMs) > 0
      ? window.setTimeout(() => controller.abort(), Number(timeoutMs))
      : null;

  let response;

  try {
    response = await fetch(url, {
      ...rest,
      headers,
      signal: controller ? controller.signal : rest.signal,
    });
  } catch (error) {
    if (timeoutId) {
      window.clearTimeout(timeoutId);
    }

    if (error?.name === "AbortError" && Number(timeoutMs) > 0) {
      const timeoutError = new Error("Request timed out. Please try again.");
      timeoutError.name = "TimeoutError";
      throw timeoutError;
    }

    throw error;
  }

  if (timeoutId) {
    window.clearTimeout(timeoutId);
  }

  if (
    response.status === 401 &&
    !skipUnauthorizedHandler &&
    (shouldUseAuth || (auth && isApiUrl(url)))
  ) {
    onUnauthorized();
  }

  return response;
};

export const readJsonSafe = async (response) => {
  try {
    return await response.json();
  } catch {
    return {};
  }
};
